/**
 * Vite-app shim for the `next/navigation` exports used by the shared
 * `@qrody/qr-generator` package — `useSearchParams` (read-only URL
 * params) and `useRouter` (push-based imperative nav). Both are
 * served by the browser-native History API; the package only uses
 * them to read `?compare=1` and to push to `/{locale}/codes/{id}`
 * after a save, neither of which needs TanStack-Router's typed
 * navigation.
 *
 * Vite + TS aliases redirect `next/navigation` to this module.
 */
import { useEffect, useState } from "react";

/**
 * Minimal `URLSearchParams` accessor for the current location. Mirrors
 * the subset of next/navigation that the generator package uses:
 * `useSearchParams().get(key)`. The hook re-reads on browser back/forward
 * (popstate) so callers stay live with the URL bar.
 */
export function useSearchParams(): URLSearchParams {
  const [params, setParams] = useState<URLSearchParams>(() => readParams());
  useEffect(() => {
    const update = () => setParams(readParams());
    window.addEventListener("popstate", update);
    return () => window.removeEventListener("popstate", update);
  }, []);
  return params;
}

function readParams(): URLSearchParams {
  if (typeof window === "undefined") return new URLSearchParams();
  return new URLSearchParams(window.location.search);
}

/**
 * Stand-in for `next/navigation`'s `useRouter`. Returns the small slice
 * of the imperative router API the generator package touches —
 * `push(href)` / `replace(href)` / `refresh()`. Both navigations go
 * through `window.location` because the targets are app-level paths
 * (e.g. `/{locale}/codes/{id}`) that we want resolved by the regular
 * routing stack rather than smuggled past it.
 *
 * NOTE: the generator's `save-action.tsx` is exported from the package
 * but web doesn't import it (we render our own save button), so
 * this hook is currently only kept for completeness; remove if it
 * becomes the last reference to the shim.
 */
export interface NextRouterShim {
  push: (href: string) => void;
  replace: (href: string) => void;
  refresh: () => void;
}

export function useRouter(): NextRouterShim {
  return {
    push: (href) => {
      window.location.assign(href);
    },
    replace: (href) => {
      window.location.replace(href);
    },
    refresh: () => {
      window.location.reload();
    },
  };
}
